import type {
  CatalogKind,
  EngineeringProduct,
  ProductAgeGroup,
  ProductGender,
} from './types'

export const MANUFACTURED_PRODUCT_TYPES = [
  'manufactured_good',
  'finished_good',
  'semi_finished_good',
  'semi_finished',
] as const

/** Translation key of every product type the catalog can store. */
export const PRODUCT_TYPE_LABEL_KEYS: Record<string, string> = {
  manufactured_good: 'engineering.catalog.manufacturedGood',
  finished_good: 'engineering.catalog.finishedGood',
  semi_finished_good: 'engineering.catalog.semiFinishedGood',
  semi_finished: 'engineering.catalog.semiFinished',
  raw_material: 'engineering.catalog.typeRawMaterial',
  spare_part: 'engineering.catalog.typeSparePart',
}

/**
 * Localised label of a product type. Pages used to print the raw enum value
 * (`semi_finished_good`) whenever a product had no group, so every screen that
 * shows a type goes through this helper.
 */
export function productTypeLabel(
  type: string | null | undefined,
  translate: (key: string) => string,
): string {
  if (!type) return ''
  const key = PRODUCT_TYPE_LABEL_KEYS[type]
  return key ? translate(key) : type
}

export function isManufacturedProduct(
  product: Pick<EngineeringProduct, 'type'>,
): boolean {
  return MANUFACTURED_PRODUCT_TYPES.includes(
    product.type as (typeof MANUFACTURED_PRODUCT_TYPES)[number],
  )
}

export function productDetailPath(
  product: Pick<EngineeringProduct, 'id' | 'type'>,
): string {
  if (product.type === 'raw_material') {
    return `/raw-materials/${product.id}`
  }
  if (product.type === 'spare_part') {
    return `/spare-parts/${product.id}`
  }
  return `/products/${product.id}`
}

export function catalogTypeForKind(kind: CatalogKind): string | undefined {
  if (kind === 'raw-materials') {
    return 'raw_material'
  }
  if (kind === 'spare-parts') {
    return 'spare_part'
  }
  return undefined
}

export function resolveAudienceSegment(
  ageGroup: ProductAgeGroup,
  gender: ProductGender,
): string | undefined {
  if (ageGroup === 'kids' || ageGroup === 'teens') {
    return ageGroup
  }
  return gender || undefined
}

export function matchesProductDemographics(
  product: Pick<EngineeringProduct, 'audience_segment'>,
  ageGroup: ProductAgeGroup,
  gender: ProductGender,
): boolean {
  const segment = product.audience_segment
  if (gender && segment !== gender) {
    return false
  }
  if (ageGroup === 'kids' || ageGroup === 'teens') {
    return segment === ageGroup
  }
  if (ageGroup === 'adult') {
    return segment === 'men' || segment === 'women'
  }
  return true
}

export function formatIdentifier(
  value: string | null | undefined,
  fallback = 'Not assigned',
): string {
  return value?.trim() || fallback
}

export function resolveAssetUrl(assetUrl: string | null): string | null {
  if (!assetUrl) {
    return null
  }
  if (/^https?:\/\//i.test(assetUrl)) {
    return assetUrl
  }
  // Inline images (uploaded directly through the passport form) are already complete.
  if (/^data:image\//i.test(assetUrl)) {
    return assetUrl
  }

  const configuredBase = String(
    import.meta.env.VITE_API_ASSET_BASE_URL || 'https://api.yuksalish.apnet.uz',
  ).replace(/\/$/, '')
  return `${configuredBase}/${assetUrl.replace(/^\//, '')}`
}

export function resolveProductImage(photoUrl: string | null): string | null {
  return resolveAssetUrl(photoUrl)
}
